import { useState } from "react";
import {
  Button,
  EmptyState,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Text,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifyError } from "@marekl/adminer/api";
import {
  useCreateClassTable,
  useCreatePerformanceClass,
  useDeleteClassTable,
  useDeletePerformanceClass,
  useUpdateClassTable,
  useUpdatePerformanceClass,
  type PerformanceClass,
  type PerformanceClassTable,
  useDuplicateClassTable,
} from "../../../lib/queries/rules";
import { ComponentCard } from "./ComponentCard";
import { reorderRows } from "./reorder";
import tableStyles from "./componentTables.module.css";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "table"; table?: PerformanceClassTable }
  | { kind: "row"; tableId: ID; row?: PerformanceClass };

interface RowForm {
  code: string;
  name: string;
}

const emptyRowForm: RowForm = { code: "", name: "" };

export function ClassTablesTab({
  ruleSetId,
  readOnly,
  lockedTableIds,
  tables,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  /**
   * Ids of the tables whose rungs may no longer be deleted: the set is in
   * service AND a discipline builds its matrix on this table.
   *
   * Per table, not per tab. A ladder nobody references holds no
   * combinations, so deleting a rung of it drops nothing and the server
   * allows it — the same reason the table's own „Smazat" stays. Hiding the
   * button on every table would have contradicted that, and left an admin
   * able to delete a whole unused ladder but not one row of it.
   */
  lockedTableIds: Set<ID>;
  tables: PerformanceClassTable[];
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [tableName, setTableName] = useState("");
  const [rowForm, setRowForm] = useState<RowForm>(emptyRowForm);

  const createTable = useCreateClassTable(ruleSetId);
  const updateTable = useUpdateClassTable();
  const deleteTable = useDeleteClassTable();
  const duplicateTable = useDuplicateClassTable();
  const createRow = useCreatePerformanceClass();
  const updateRow = useUpdatePerformanceClass();
  const deleteRow = useDeletePerformanceClass();

  const close = () => {
    setModal({ kind: "closed" });
    createTable.reset();
    updateTable.reset();
    duplicateTable.reset();
    createRow.reset();
    updateRow.reset();
  };

  const openTable = (table?: PerformanceClassTable) => {
    setTableName(table?.name ?? "");
    createTable.reset();
    updateTable.reset();
    setModal({ kind: "table", table });
  };

  const openRow = (tableId: ID, row?: PerformanceClass) => {
    setRowForm(row ? { code: row.code, name: row.name } : emptyRowForm);
    createRow.reset();
    updateRow.reset();
    setModal({ kind: "row", tableId, row });
  };

  const submitTable = () => {
    if (modal.kind !== "table") return;
    const payload = { name: tableName.trim() };
    if (modal.table) {
      updateTable.mutate({ id: modal.table.id, payload }, { onSuccess: close });
    } else {
      createTable.mutate(payload, { onSuccess: close });
    }
  };

  const submitRow = () => {
    if (modal.kind !== "row") return;
    const payload = {
      code: rowForm.code.trim(),
      name: rowForm.name.trim(),
      sort_order:
        modal.row?.sort_order ??
        (tables.find((t) => t.id === modal.tableId)?.classes ?? []).length,
    };
    if (modal.row) {
      updateRow.mutate({ id: modal.row.id, payload }, { onSuccess: close });
    } else {
      createRow.mutate({ tableId: modal.tableId, payload }, { onSuccess: close });
    }
  };

  const move = (table: PerformanceClassTable, index: number, direction: -1 | 1) => {
    for (const { row, sort_order } of reorderRows(table.classes ?? [], index, direction)) {
      updateRow.mutate({
        id: row.id,
        payload: { code: row.code, name: row.name, sort_order },
      }, { onError: (err) => notifyError(err) });
    }
  };

  return (
    <>
      <Page.SectionHeader
        title="Tabulky výkonnostních tříd"
        meta={
          readOnly ? undefined : (
            <Button variant="primary" onClick={() => openTable()}>
              Nová tabulka
            </Button>
          )
        }
      />
      <Text variant="muted">
        Výkonnostní třída je třetí osa kombinací. Disciplína soutěžená v jediné třídě
        použije tabulku s jedním řádkem; co třída znamená (vlastní čas, počet porotců
        nebo omezení prvků) se nastavuje na jejích kombinacích.
      </Text>

      {tables.length === 0 ? (
        <EmptyState
          title="Žádné tabulky výkonnostních tříd"
          description="Založte tabulku a vypište třídy, ve kterých se soutěží (A, B, …)."
        />
      ) : (
        tables.map((table) => (
          <ComponentCard<PerformanceClass>
            key={table.id}
            title={table.name}
            subtitle={<Text variant="muted">{(table.classes ?? []).length} {pluralizeCzech((table.classes ?? []).length, ["třída", "třídy", "tříd"])}</Text>}
            readOnly={readOnly}
          lockDelete={lockedTableIds.has(table.id)}
            rows={table.classes ?? []}
            emptyTitle="Zatím bez tříd"
            emptyDescription="Přidejte první výkonnostní třídu."
            addLabel="Přidat třídu"
            columns={[
              {
                key: "code",
                header: "Kód",
                tdClass: tableStyles.code,
                render: (r) => <Text variant="mono">{r.code}</Text>,
              },
              { key: "name", header: "Název", tdClass: tableStyles.name, render: (r) => r.name },
            ]}
            onEditTable={() => openTable(table)}
            onDuplicateTable={() => duplicateTable.mutate({ id: table.id }, { onError: (err) => notifyError(err) })}
            onDeleteTable={() =>
              confirm({
                title: "Smazat tabulku výkonnostních tříd",
                description: (
                  <>
                    Smaže se tabulka <strong>{table.name}</strong> i všechny její třídy.
                    Tabulku, kterou používá některá disciplína, smazat nelze.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteTable.mutateAsync(table.id),
              })
            }
            onAddRow={() => openRow(table.id)}
            onEditRow={(row) => openRow(table.id, row)}
            onDeleteRow={(row) =>
              confirm({
                title: "Smazat výkonnostní třídu",
                description: (
                  <>
                    Smaže se třída <strong>{row.name}</strong>. Ve všech disciplínách, které tuto
                    tabulku používají, zaniknou i kombinace v této třídě, včetně jejich vlastního
                    nastavení.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteRow.mutateAsync(row.id),
              })
            }
            onMoveRow={(index, direction) => move(table, index, direction)}
          />
        ))
      )}

      <FormModal
        open={modal.kind === "table"}
        title={
          modal.kind === "table" && modal.table
            ? "Upravit tabulku"
            : "Nová tabulka výkonnostních tříd"
        }
        onClose={close}
        submitLabel="Uložit"
        busy={createTable.isPending || updateTable.isPending}
        canSubmit={!!tableName.trim()}
        error={createTable.error || updateTable.error}
        onSubmit={submitTable}
      >
        <Input
          label="Název tabulky"
          required
          placeholder="Třídy A a B"
          value={tableName}
          onChange={(e) => setTableName(e.target.value)}
        />
      </FormModal>

      <FormModal
        open={modal.kind === "row"}
        title={modal.kind === "row" && modal.row ? "Upravit třídu" : "Nová výkonnostní třída"}
        onClose={close}
        submitLabel="Uložit"
        busy={createRow.isPending || updateRow.isPending}
        canSubmit={!!rowForm.code.trim() && !!rowForm.name.trim()}
        error={createRow.error || updateRow.error}
        onSubmit={submitRow}
      >
        <ResponsiveGrid columns={2}>
          <Input
            label="Kód"
            required
            placeholder="B"
            value={rowForm.code}
            onChange={(e) => setRowForm({ ...rowForm, code: e.target.value })}
          />
          <Input
            label="Název"
            required
            placeholder="Výkonnostní třída B"
            value={rowForm.name}
            onChange={(e) => setRowForm({ ...rowForm, name: e.target.value })}
          />
        </ResponsiveGrid>
      </FormModal>


    </>
  );
}
